import { Box, Typography, useTheme, Breadcrumbs } from "@mui/material";
import { useNavigate } from "react-router-dom";

import AssessmentIcon     from "@mui/icons-material/Assessment";
import StoreIcon          from "@mui/icons-material/Store";
import InventoryIcon      from "@mui/icons-material/Inventory";
import ReceiptIcon        from "@mui/icons-material/Receipt";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import ChevronRightIcon   from "@mui/icons-material/ChevronRight";

/* ── Single report link ── */
const ReportLink = ({ label, path, navigate, accent, textPrimary, textSecondary }) => (
  <Box
    onClick={() => navigate(path)}
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      px: "0.875rem",
      py: "0.6rem",
      borderRadius: "7px",
      cursor: "pointer",
      color: textSecondary,
      transition: "all 0.15s",
      "&:hover": {
        backgroundColor: "rgba(29,158,117,0.08)",
        color: textPrimary,
        "& .arrow": { opacity: 1, transform: "translateX(2px)" },
      },
    }}
  >
    <Typography sx={{ fontSize: "13px" }}>{label}</Typography>
    <ChevronRightIcon
      className="arrow"
      sx={{ fontSize: "16px", color: accent, opacity: 0, transition: "all 0.15s" }}
    />
  </Box>
);

/* ── Report category card ── */
const ReportCard = ({ icon, title, reports, navigate, accent, textPrimary, textSecondary, cardBg, borderColor }) => (
  <Box
    sx={{
      backgroundColor: cardBg,
      border: `1px solid ${borderColor}`,
      borderRadius: "12px",
      overflow: "hidden",
      flex: "1 1 260px",
      minWidth: "220px",
      maxWidth: "360px",
    }}
  >
    {/* Card header */}
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "10px",
        px: "1.25rem",
        py: "1rem",
        borderBottom: `1px solid ${borderColor}`,
        backgroundColor: "rgba(29,158,117,0.05)",
      }}
    >
      <Box sx={{
        width: "32px", height: "32px",
        borderRadius: "8px",
        backgroundColor: "rgba(29,158,117,0.12)",
        display: "flex", alignItems: "center", justifyContent: "center",
        color: accent, flexShrink: 0,
      }}>
        {icon}
      </Box>
      <Box>
        <Typography sx={{
          fontSize: "10px", fontWeight: 500,
          letterSpacing: "0.1em", textTransform: "uppercase",
          color: accent,
        }}>
          {title}
        </Typography>
        <Typography sx={{ fontSize: "11px", color: textSecondary, mt: "1px" }}>
          {reports.length} report{reports.length !== 1 ? "s" : ""}
        </Typography>
      </Box>
    </Box>

    {/* Report links */}
    <Box sx={{ px: "0.5rem", py: "0.75rem", display: "flex", flexDirection: "column", gap: "2px" }}>
      {reports.map(({ label, path }) => (
        <ReportLink
          key={path} label={label} path={path} navigate={navigate}
          accent={accent} textPrimary={textPrimary} textSecondary={textSecondary}
        />
      ))}
    </Box>
  </Box>
);

/* ── Main page ── */
const ReportsForm = () => {
  const navigate = useNavigate();
  const theme    = useTheme();
  const isDark   = theme.palette.mode === "dark";

  const bodyBg      = theme.palette.background.alt;          // lighter body — F6F6F6 light / #1e2d24 dark
  const cardBg      = isDark ? "rgba(255,255,255,0.03)" : theme.palette.grey?.[0] ?? "#ffffff";
  const accent      = theme.palette.secondary.main;           // #1D9E75
  const textPrimary = theme.palette.neutral.dark;             // dark text in light mode, light in dark
  const textSecondary = theme.palette.neutral.main;
  const borderColor = isDark ? "rgba(29,158,117,0.14)" : "rgba(29,158,117,0.18)";
  const stripBg     = isDark ? "rgba(29,158,117,0.08)" : "rgba(29,158,117,0.06)";
  const statNumColor = theme.palette.neutral.dark;
  const breadcrumbMuted = isDark ? "rgba(255,255,255,0.3)" : theme.palette.neutral.main;

  const categories = [
    {
      icon:    <StoreIcon sx={{ fontSize: "18px" }} />,
      title:   "Sales",
      reports: [
        { label: "Item Sales Detail Report",    path: "/itemSalesReport"          },
        { label: "Item Sales Summative Report", path: "/itemSalesSummativeReport" },
        { label: "Customer Refunds Report",     path: "/customerReturnReport"     },
      ],
    },
    {
      icon:    <InventoryIcon sx={{ fontSize: "18px" }} />,
      title:   "Inventory",
      reports: [
        { label: "Item Trends",             path: "/itemTrends"               },
        { label: "Batchwise Stock Report",  path: "/batchwiseItemStockReport"  },
        { label: "Dead Stock Report",       path: "/deadStockReport"           },
        { label: "Stock Adjustment Report", path: "/stockAdjustmentReport"     },
      ],
    },
    {
      icon:    <ReceiptIcon sx={{ fontSize: "18px" }} />,
      title:   "Purchase",
      reports: [
        { label: "Item Purchase Detail Report",    path: "/itemPurchaseDetailReport"    },
        { label: "Item Purchase Summative Report", path: "/itemPurchaseSummativeReport" },
        { label: "Supplier Returns Detail Report", path: "/supplierReturnReport"        },
      ],
    },
    {
      icon:    <AccountBalanceIcon sx={{ fontSize: "18px" }} />,
      title:   "Accounting",
      reports: [
        { label: "Profit & Loss", path: "/profit&Loss"  },
        { label: "Balance Sheet", path: "/balanceSheet" },
      ],
    },
  ];

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: bodyBg, p: "1.5rem 2rem" }}>

      {/* ── Page header ── */}
      <Box sx={{ mb: "2rem" }}>
        <Breadcrumbs
          separator={<ChevronRightIcon sx={{ fontSize: "14px", color: borderColor }} />}
          sx={{ mb: "0.5rem" }}
        >
          <Typography sx={{ fontSize: "12px", color: breadcrumbMuted }}>Home</Typography>
          <Typography sx={{ fontSize: "12px", color: accent }}>Reports</Typography>
        </Breadcrumbs>

        <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <Box sx={{
            width: "38px", height: "38px",
            borderRadius: "10px",
            backgroundColor: "rgba(29,158,117,0.12)",
            display: "flex", alignItems: "center", justifyContent: "center",
            color: accent, flexShrink: 0,
          }}>
            <AssessmentIcon sx={{ fontSize: "20px" }} />
          </Box>
          <Box>
            <Typography sx={{
              fontFamily: "'DM Serif Display', serif",
              fontSize: "22px", fontWeight: 400,
              color: textPrimary, lineHeight: 1.2,
            }}>
              Reports
            </Typography>
            <Typography sx={{ fontSize: "12px", color: textSecondary, mt: "2px" }}>
              {categories.reduce((s, c) => s + c.reports.length, 0)} reports across {categories.length} categories
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* ── Stat strip ── */}
      <Box sx={{
        display: "flex",
        gap: "1px",
        backgroundColor: borderColor,
        border: `1px solid ${borderColor}`,
        borderRadius: "10px",
        overflow: "hidden",
        mb: "2rem",
      }}>
        {categories.map((cat) => (
          <Box key={cat.title} sx={{
            flex: 1,
            px: "1.25rem", py: "0.875rem",
            backgroundColor: stripBg,
            display: "flex", flexDirection: "column", gap: "3px",
          }}>
            <Typography sx={{ fontSize: "18px", fontWeight: 500, color: statNumColor }}>
              {cat.reports.length}
            </Typography>
            <Typography sx={{ fontSize: "11px", color: accent, letterSpacing: "0.06em", textTransform: "uppercase" }}>
              {cat.title}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* ── Cards grid ── */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: "1rem" }}>
        {categories.map((cat) => (
          <ReportCard
            key={cat.title}
            icon={cat.icon}
            title={cat.title}
            reports={cat.reports}
            navigate={navigate}
            accent={accent}
            textPrimary={textPrimary}
            textSecondary={textSecondary}
            cardBg={cardBg}
            borderColor={borderColor}
          />
        ))}
      </Box>
    </Box>
  );
};

export default ReportsForm;